/**
 * <tecton-gallery> — PDP media gallery.
 *
 * Thumbnail strip drives a stacked stage. Deliberately small: Horizon ships a
 * full media-gallery component, but it carries zoom, slideshow and layout modes
 * this design does not use, and its markup is hard to restyle into the Tecton
 * card language. This is ~60 lines against ~600.
 *
 * Handles: click, arrow-key roving focus (it is a real tablist), swipe on
 * touch, and pausing any video when you navigate away from its slide.
 */

class TectonGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    if (this.slides.length < 2) return;

    this.index = 0;

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.thumb)));
      thumb.addEventListener('keydown', this.onKeydown);
    }

    this.bindSwipe();
  }

  onKeydown = (event) => {
    const delta = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity }[event.key];
    if (delta === undefined) return;
    event.preventDefault();

    let next;
    if (delta === -Infinity) next = 0;
    else if (delta === Infinity) next = this.slides.length - 1;
    else next = (this.index + delta + this.slides.length) % this.slides.length;

    this.show(next);
    this.thumbs[next]?.focus();
  };

  bindSwipe() {
    const stage = this.querySelector('.tecton-pdp__stage');
    if (!stage) return;

    let startX = null;
    stage.addEventListener(
      'touchstart',
      (e) => {
        startX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) < 40) return;
        const dir = dx < 0 ? 1 : -1;
        this.show((this.index + dir + this.slides.length) % this.slides.length);
      },
      { passive: true }
    );
  }

  show(index) {
    if (index === this.index) return;

    // A video left playing behind a hidden slide keeps burning bandwidth and,
    // if it has audio, keeps making noise.
    this.slides[this.index]?.querySelector('video')?.pause();

    this.index = index;

    this.slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-selected', String(i === index));
      thumb.tabIndex = i === index ? 0 : -1;
    });

    this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('tecton-gallery')) {
  customElements.define('tecton-gallery', TectonGallery);
}
